<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BSidebar
      id="sbRsmReportTasks"
      backdrop-variant="dark"
      backdrop
      shadow
      right
    >
      <template #title>
        <i class="fad fa-filter-list" /> Filters
      </template>

      <BContainer>
        <BRow>
          <BCol
            cols="12"
          >
            <BFormGroup
              label="Month"
              label-for="filterMonth"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <flat-pickr
                id="filterMonth"
                v-model="tableTasks.filter.month"
                class="form-control"
                placeholder="--------- ----"
                :config="flatPickerConfig"
              />
            </BFormGroup>
          </BCol>
          <BCol
            cols="12"
          >
            <BFormGroup
              label="Area"
              label-for="filterArea"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <VSelect
                v-model="tableTasks.selected.area"
                placeholder="filter area here"
                input-id="filterArea"
                label="area_name"
                class="border border-light rounded-lg"
                clearable
                append-to-body
                :options="(tableTasks.options.areas)"
                :loading="(tableTasks.fetching.areas)"
                :disabled="(tableTasks.busy || tableTasks.fetching.areas)"
                :calculate-position="calculateDropPosition"
                @search="searchFilterAreas"
              >
                <template #no-options="">
                  no available area
                </template>
              </VSelect>
            </BFormGroup>
          </BCol>

          <BCol
            cols="12"
          >
            <BFormGroup
              label="Officer"
              label-for="filterOfficer"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <VSelect
                v-model="tableTasks.selected.fsOfficer"
                placeholder="filter officer here"
                input-id="filterOfficer"
                label="officer_name"
                clearable
                append-to-body
                :options="(tableTasks.options.fsOfficers)"
                :loading="(tableTasks.fetching.fsOfficers)"
                :disabled="(tableTasks.busy || tableTasks.fetching.fsOfficers)"
                :calculate-position="calculateDropPosition"
                @search="searchFilterFSOfficers"
              >
                <template #option="{ officer_name, mobile_number, photo_url }">
                  <div class="d-flex flex-column flex-md-row py-1">
                    <div class="flex-grow-1 d-flex align-items-center">
                      <div class="mr-2">
                        <BAvatar
                          v-if="photo_url"
                          :src="getOfficerProfile(photo_url)"
                          variant="primary"
                          size="28px"
                        />
                        <BAvatar
                          v-else
                          :text="String(officer_name).charAt(0)"
                          variant="primary"
                          size="28px"
                        />
                      </div>
                      <div class="d-flex flex-column">
                        <div
                          class="text-break text-wrap"
                          v-text="officer_name"
                        />
                        <strong>{{ mobile_number || '' }}</strong>
                      </div>
                    </div>
                  </div>
                </template>
                <template #selected-option="{ officer_name, photo_url }">
                  <div class="d-flex flex-column flex-md-row">
                    <div class="flex-grow-1 d-flex align-items-center">
                      <div class="mr-2">
                        <BAvatar
                          v-if="photo_url"
                          :src="getOfficerProfile(photo_url)"
                          variant="primary"
                          size="28px"
                        />
                        <BAvatar
                          v-else
                          :text="String(officer_name).charAt(0)"
                          variant="primary"
                          size="28px"
                        />
                      </div>
                      <div
                        class="text-break text-wrap"
                        v-text="officer_name"
                      />
                    </div>
                  </div>
                </template>
                <template #no-options="">
                  no available officer
                </template>
              </VSelect>
            </BFormGroup>
          </BCol>
        </BRow>
      </BContainer>
    </BSidebar>

    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BInputGroup>
            <BFormInput
              id="filterSearch"
              v-model="tableTasks.filter.search"
              placeholder="search here"
              autocomplete="off"
              debounce="1000"
              type="text"
            />
            <BInputGroupAppend>
              <BButton
                v-b-toggle.sbRsmReportTasks
                variant="primary"
              >
                <i
                  v-if="[settingFilters, tableTasks.fetching.fsOfficers].some(isTrue => isTrue)"
                  class="fad fa-spinner fa-spin p-0"
                />
                <i
                  v-else
                  class="fad fa-filter-list"
                /> Filters
              </BButton>
            </BInputGroupAppend>
          </BInputGroup>
        </BFormGroup>
      </BCol>
      <BCol>
        <BOverlay
          rounded
          opacity="0.1"
          spinner-small
          class="d-inline"
          spinner-variant="warning"
          :show="exporter.excel.exporting"
          style="margin-bottom: 1.1px;"
        >
          <JsonExcel
            :class="[
              'btn btn-sm btn-primary', {
                'btn-outline-primary disabled': exporter.excel.exporting || !isValidForExport
              }
            ]"
            :fetch="excelProvider"
            :before-generate="onStartExcelExport"
            :before-finish="onFinishExcelExport"
            :name="excelFileName"
            :fields="exporter.excel.fields"
            :escape-csv="exporter.excel.escape"
            :type="exporter.excel.type"
            :header="exporter.excel.header"
            :footer="exporter.excel.footer"
            worksheet="Tasks"
          >
            {{ exporter.excel.exporting ? 'Exporting' : 'Export As Excel' }}
          </JsonExcel>
        </BOverlay>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableTasks"
      class="mt-3"
      :provider="tableProvider"
      :fields="tableTasks.fields"
      :filter="tableTasks.filter"
      :filter-on="tableTasks.filterOn"
      :sort-by="tableTasks.sortBy"
      :sort-desc="tableTasks.sortDesc"
      :sort-direction="tableTasks.sortDirection"
      :total-rows="tableTasks.totalRows"
      :current-page="tableTasks.currentPage"
      :per-page="tableTasks.perPage"
      :stacked="tableTasks.stacked"
      :busy="tableTasks.busy"
    >

      <template #cell(status)="{ value }">
        <BBadge
          class="w-60px"
          :variant="`${customerStatus(value)}-light`"
          pill
          v-text="value"
        />
      </template>
    </CustomTable>
  </BContainer>
</template>
<script>
import QUERY from 'lodash'
import { mapGetters } from 'vuex'
import MOMENT from 'moment'
import FPMonthSelect from 'flatpickr/dist/plugins/monthSelect'
import { setTableOptions, getTableOptions } from '@/forage/rsm/officers/reports/tasks'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MxImageUrl from '@/mixins/image-url'
import MAreas from '@/mixins/locations/areas'
import MFSOfficers from '@/mixins/accounts/fsos'

import { SRSMFsoTask } from '@/services/rsm'

export default {

  name: 'RSMOfficerReportTasks',

  mixins: [
    MAreas,
    MFSOfficers,
    MxMisc,
    MxFormatter,
    MxImageUrl
  ],

  beforeRouteEnter (_to, _from, navigate) {
    getTableOptions().then(options => {
      navigate(context => {
        if (context.currentUser) {
          context.state.settings = true

          context.tableTasks.selected.team = {
            id: context.currentUser.team
          }

          if (options) {
            const { currentPage, perPage, sortBy, sortDesc, totalRows, filter } = options

            context.tableTasks = Object.assign(context.tableTasks, {
              totalRows: totalRows || 0,
              sortBy: sortBy || 'id',
              sortDesc: sortDesc || false,
              perPage: perPage || 10,
              currentPage: currentPage || 1,
              filter: {
                search: filter?.search || '',
                month: filter?.month || '',
                area: '',
                officer: ''
              },
              editable: {
                area: filter?.area || '',
                fsOfficer: filter?.officer || ''
              }
            })
          } else {
            context.tableTasks.currentPage = 1
          }

          context.$nextTick(() => {
            context.getAreas(context.tableTasks, '', context.currentUser.team).finally(() => {
              if (context.tableTasks.editable.area) {
                context.tableTasks.selected.area = QUERY.find(context.tableTasks.options.areas, {
                  id: context.tableTasks.editable.area
                }) || ''

                context.tableTasks.editable.area = ''
              } else {
                context.getFSOfficers(
                  context.tableTasks,
                  '',
                  context.currentUser.team,
                  context.tableTasks.editable.area
                ).finally(() => {
                  if (context.tableTasks.editable.fsOfficer) {
                    context.tableTasks.selected.fsOfficer = QUERY.find(context.tableTasks.options.fsOfficers, {
                      id: context.tableTasks.editable.fsOfficer
                    }) || ''

                    context.tableTasks.editable.fsOfficer = ''
                  }
                })
              }

              context.state.settings = false
            })
          })
        }
      })
    })
  },

  middleware: [
    'auth',
    'rsm'
  ],

  metaInfo: () => ({
    title: 'Report - Tasks'
  }),

  data () {
    return {
      flatPickerConfig: {
        appendTo: document.body,
        altInput: true,
        allowInput: true,
        disableMobile: true,
        plugins: [
          new FPMonthSelect({
            dateFormat: 'Y-m',
            altFormat: 'F Y'
          })
        ]
      },
      state: {
        busy: false,
        editing: false
      },
      exporter: {
        excel: {
          exporting: false,
          header: [],
          escape: false,
          type: 'xls',
          fields: {
            'Created At': {
              field: 'created_at',
              callback: createdAt => `="${this.dateTimeFormatter(createdAt)}"`
            },
            Schedule: {
              field: 'schedule'
            },
            Area: {
              field: 'assignee.area.area_name'
            },
            'Area Manager': {
              field: 'creator',
              callback: creator => {
                return `${creator?.first_name} ${creator?.last_name}`
              }
            },
            Officer: {
              field: 'assignee',
              callback: assignee => {
                return `${assignee?.first_name} ${assignee?.last_name}`
              }
            },
            Activity: {
              field: 'activity.activity_name'
            },
            'Event Plan': {
              field: 'event_plan',
              callback: eventPlan => eventPlan || 0
            },
            'Actual Events': {
              field: 'events_count',
              callback: eventsCount => eventsCount || 0
            },
            'Event Progress': {
              field: '',
              callback: row => row.event_plan > 0 ? `${this.numberFormatter(row.events_count / row.event_plan * 100)}%` : '0.00%'
            }
          },
          footer: []
        }
      },
      tableTasks: {
        busy: false,
        fetching: {
          areas: false,
          fsOfficers: false
        },
        options: {
          areas: [],
          fsOfficers: []
        },
        selected: {
          area: '',
          fsOfficer: ''
        },
        editable: {
          area: '',
          fsOfficer: ''
        },
        filter: {
          search: '',
          month: MOMENT().format('YYYY-MM'),
          area: '',
          officer: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 0,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-right stacked-full text-md-center' },
          // { mobile: 10, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'schedule', label: 'Month', class: 'min-w-75px', formatter: this.monthLongFormatter },
          { mobile: 2, key: 'assignee.area.area_name', label: 'Area', class: 'min-w-250px' },
          { mobile: 4, key: 'creator', label: 'Area Manager', class: 'min-w-md-250px', formatter: creator => this.getFullName(null, null, creator), sortable: true },
          { mobile: 5, key: 'assignee', label: 'Technical Officer', class: 'min-w-md-250px', formatter: assignee => this.getFullName(null, null, assignee), sortable: true },
          { mobile: 6, key: 'activity.activity_name', label: 'Activity', class: 'min-w-md-250px' },
          { mobile: 8, key: 'event_plan', label: 'Event Plan', class: 'text-center' },
          { mobile: 9, key: 'events_count', label: 'Actual Events', class: 'text-center' },
          {
            mobile: 10,
            key: 'event_progress',
            label: 'Event Progress',
            class: 'text-center',
            formatter: (_v, _k, row) => {
              return row.event_plan > 0 ? `${this.numberFormatter(row.events_count / row.event_plan * 100)}%` : '0.00%'
            }
          }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    excelFileName () {
      const month = MOMENT(this.tableTasks.filter.month).format('YYYY-MM')
      return `Tasks ${month}`
    },

    isValidForExport () {
      const isDateFromNotEmpty = ![null, '', ' '].includes(this.tableTasks.filter.month)

      return isDateFromNotEmpty && Number(this.tableTasks.totalRows) >= 1
    },

    settingFilters () {
      const hasEditables = Object.keys(this.tableTasks.editable).map(
        editableKey => this.tableTasks.editable[editableKey]
      ).some(val => String(val) !== '')

      return hasEditables
    }
  },

  watch: {
    'tableTasks.selected.area' (area) {
      this.tableTasks.filter.area = area?.id || ''
    },

    'tableTasks.selected.fsOfficer' (fsOfficer) {
      this.tableTasks.filter.officer = fsOfficer?.id || ''
    },

    'tableTasks.filter.area' (area) {
      this.getFSOfficers(this.tableTasks, '', this.currentUser.team, area).finally(() => {
        if (this.tableTasks.editable.fsOfficer) {
          this.tableTasks.selected.fsOfficer = QUERY.find(this.tableTasks.options.fsOfficers, {
            id: this.tableTasks.editable.fsOfficer
          }) || ''

          this.tableTasks.editable.fsOfficer = ''
        }
      })
    }
  },

  methods: {

    customerStatus (status) {
      switch (status) {
        case 'Verified': {
          return 'secondary'
        }
        case 'New': {
          return 'primary'
        }
        default: {
          return 'warning'
        }
      }
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      if (this.settingFilters) {
        return []
      }

      this.tableTasks.busy = true
      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        assignee_id: filter.officer,
        team_id: this.currentUser.team,
        area_id: filter.area,
        schedule: filter.month
      }

      return await SRSMFsoTask.reports(payload).then(
        ({ data }) => {
          this.tableTasks.totalRows = data.total_rows
          setTableOptions({
            currentPage,
            perPage,
            sortBy,
            sortDesc,
            filter,
            totalRows: data.total_rows
          })
          return data.items
        }
      ).catch(() => {
        this.tableTasks.totalRows = 0
        setTableOptions({
          currentPage,
          perPage,
          sortBy,
          sortDesc,
          filter,
          totalRows: 0
        })
        return []
      }).finally(() => {
        this.tableTasks.busy = false
      })
    },

    async searchFilterAreas (search, loading) {
      this.searchFilterAreas(this.tableTasks, search, loading)
    },

    async searchFilterFSOfficers (search, loading) {
      this.searchFSOfficersProvider(this.tableTasks, search, loading)
    },

    async excelProvider () {
      if (this.exporter.busy || !this.isValidForExport) {
        return null
      }

      this.exporter.busy = this.exporter.excel.exporting = true

      const payload = {
        filter_text: this.tableTasks.filter.search,
        assignee_id: this.tableTasks.filter.officer,
        team_id: this.currentUser.team,
        area_id: this.tableTasks.filter.area,
        schedule: this.tableTasks.filter.month
      }

      return await SRSMFsoTask.exports(payload).then(
        ({ data: { items } }) => {
          if (items.length <= 0) {
            this.swalInvalid(
              'No available data to be Exported'
            )
          }

          return items
        }
      ).catch(() => {
        return []
      }).finally(() => {
        this.exporter.busy = this.exporter.excel.exporting = false
      })
    },

    onStartExcelExport () {
      if (this.exporter.busy || !this.isValidForExport) {
        return null
      }

      this.exporter.excel.header = []
      this.exporter.excel.footer = []
    },

    onFinishExcelExport () {
    }
  }
}
</script>
